import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  type IAsyncService,
  type IAsyncServiceParameter,
} from '../../../../expressions/repository/async/context/IAsyncService.ts';
import {
  compareAsyncServiceOutputs,
  type IAsyncServiceOutputsDiff,
} from './compareAsyncServiceOutputs.ts';
import { useHandleStaleOutputsUpdate } from './useHandleStaleOutputsUpdate.ts';

export interface IAsyncServiceStaleOutputsOptions {
  selected: IAsyncService | undefined;
  setOutputsField: (value: IAsyncServiceParameter[]) => void;
  setLabelFields: (value: string[]) => void;
  setValueField: (value: string | null) => void;
  onAfterUpdate?: (diff: IAsyncServiceOutputsDiff) => void;
}

export interface IAsyncServiceStaleOutputsResult {
  diff: IAsyncServiceOutputsDiff | null;
  handleUpdate: () => void;
}

/**
 * Custom hook per gestire la logica di rilevamento e aggiornamento dei campi
 * in seguito a modifiche nei servizi asincroni selezionati.
 */
export function useAsyncServiceStaleOutputs(
  options: IAsyncServiceStaleOutputsOptions,
): IAsyncServiceStaleOutputsResult {
  const { selected, setOutputsField, setLabelFields, setValueField } = options;

  const storedOutputs: IAsyncServiceParameter[] | undefined = useWatch({
    name: 'outputs',
  });
  const currentLabelFields: string[] | undefined = useWatch({
    name: 'labelFields',
  });
  const currentValueField: string | null = useWatch({ name: 'valueField' });

  const diff = useMemo(
    () =>
      selected
        ? compareAsyncServiceOutputs(storedOutputs ?? [], selected.outputs)
        : null,
    [selected, storedOutputs],
  );

  const handleUpdate = useHandleStaleOutputsUpdate({
    selected,
    diff,
    currentLabelFields,
    currentValueField,
    setOutputsField,
    setLabelFields,
    setValueField,
    onAfterUpdate: options.onAfterUpdate,
  });

  return { diff, handleUpdate };
}
